import qs from 'qs'
import axios from 'axios'
import store from '@/store'

import { Notify } from 'vant'
import { isKbApp } from '@/hooks/use-env'
import { sensorsDevApi } from '@/utils/sensors-tracker'
import { generate32UUID } from '@/utils/index'
import { autoRenewalToken } from '@/utils/user'

let apiTimeout = 5000
let apiBaseURL = process.env.VUE_APP_KB_API_HOST
if (process.env.NODE_ENV === 'development') {
    const urlParams = new URLSearchParams(window.location.search)
    const devApiTimeout = urlParams.get('apiTimeout')
    if (devApiTimeout) {
        apiTimeout = devApiTimeout * 1000
    }
    const devHttp = urlParams.get('apiHttp')
    if (devHttp) {
        apiBaseURL = apiBaseURL.replaceAll('https', 'http')
    }
}

// create an axios instance
const service = axios.create({
    baseURL: apiBaseURL,
    // withCredentials: true, // send cookies when cross-domain requests
    timeout: apiTimeout,
})

// request interceptor
service.interceptors.request.use(
    (config) => {
        config.startRequestTime = Date.now()
        // do something before request is sent
        config.headers['kb_ep'] = 201
        if (store.getters.getUserInfo.siteId) {
            config.headers['kb_sid'] = store.getters.getUserInfo.siteId
            sensors.registerPage({ kb_sid: store.getters.getUserInfo.siteId })
        }
        if (store.getters.getUserInfo.source) {
            config.headers['kb_src'] = store.getters.getUserInfo.source
        }
        if (store.getters.getUserInfo.userId) {
            config.headers['kb_uid'] = store.getters.getUserInfo.userId
            sensors.registerPage({ kb_uid: store.getters.getUserInfo.userId })
        } else {
            if (config.params && config.params.userId) {
                config.headers['kb_uid'] = config.params.userId
            } else if (config.data) {
                let qsData = qs.parse(config.data)
                if (qsData.userId) {
                    config.headers['kb_uid'] = qsData.userId
                }
            }
        }
        if (store.getters.getUserInfo.token) {
            config.headers['kb_tkn'] = store.getters.getUserInfo.token
        } else {
            if (config.params && config.params.token) {
                config.headers['kb_tkn'] = config.params.token
            } else if (config.data) {
                let qsData = qs.parse(config.data)
                if (qsData.token) {
                    config.headers['kb_tkn'] = qsData.token
                }
            }
        }
        let uuidKey = 'kb_uuid_key'
        var uuid = localStorage.getItem(uuidKey)
        if (!uuid) {
            uuid = generate32UUID()
            localStorage.setItem(uuidKey, uuid)
        }
        config.headers['kb_cid'] = uuid
        config.headers['kb_cnl'] = '201'
        if (/(iPhone|iPad|iPod|iOS)/i.test(navigator.userAgent)) {
            config.headers['kb_do'] = 'iOS'
        } else if (/(Android)/i.test(navigator.userAgent)) {
            config.headers['kb_do'] = 'Android'
        }
        if (/(kb_ios)/i.test(navigator.userAgent)) {
            config.headers['kb_os'] = 'iOS-Web'
        } else if (/(kb_android)/i.test(navigator.userAgent)) {
            config.headers['kb_os'] = 'Android-Web'
        } else if (/(MicroMessenger)/i.test(navigator.userAgent)) {
            config.headers['kb_os'] = 'WX-Web'
        } else {
            config.headers['kb_os'] = 'Browser'
        }
        return config
    },
    (error) => {
        return Promise.reject({
            error: error,
        })
    },
)

// response interceptor
service.interceptors.response.use(
    /**
     * If you want to get http information such as headers or status
     * Please return  response => response
     */
    /**
     * Determine the request status by custom code
     * Here is just an example
     * You can also judge the status by HTTP Status Code
     */
    (response) => {
        const res = response.data
        // if the custom code is not 20000, it is judged as an error.
        if (res.code === 0 || res.state === 200) {
            return res
        } else {
            if (
                res.code === 201312 &&
                (/(kb_ios)/i.test(window.navigator.userAgent) || /(kb_android)/i.test(window.navigator.userAgent))
            ) {
                autoRenewalToken()
            } else {
                !response.config.ignoreWarn && Notify({ type: 'warning', message: res.msg || 'Error', duration: 2 * 1000 })
            }
            return Promise.reject({
                errorMsg: res.msg || 'Error',
                response: response,
            })
        }
    },
    (error) => {
        const errorMsg = error.message || 'Error'

        if (error.response?.status >= 400) {
            sensorsDevApi.reportError({
                apiUrl: error.config.baseURL + error.config.url,
                apiPath: error.config.url,
                apiParam: qs.stringify(Object.assign({}, error.config.params, error.config.data)),
                costTime: Date.now() - error.config.startRequestTime,
                errorType: sensorsDevApi.DevApiErrorType.HTTP_ERROR,
                httpCode: error.response?.status,
                errorMsg: [errorMsg, isKbApp ? 'env:kbapp' : 'env:other'].join(' '),
            })
        } else {
            const costTime = Date.now() - error.config.startRequestTime
            sensorsDevApi.reportError({
                apiUrl: error.config.baseURL + error.config.url,
                apiPath: error.config.url,
                apiParam: qs.stringify(Object.assign({}, error.config.params, error.config.data)),
                costTime,
                errorType: costTime >= 5000 ? sensorsDevApi.DevApiErrorType.TIMEOUT : sensorsDevApi.DevApiErrorType.OTHER,
                errorMsg: [errorMsg, isKbApp ? 'env:kbapp' : 'env:other'].join(' '),
            })
        }

        Notify({ type: 'danger', message: errorMsg, duration: 2 * 1000 })
        return Promise.reject({
            errorMsg,
        })
    },
)

export default service
